import { useEffect } from "react";
import { toast } from "react-hot-toast";
import type { useAppModals } from "./useAppModals";
import type { useTranslator } from "../features/translator/hooks/useTranslator";

interface UseAppShortcutsProps {
  auth: ReturnType<typeof useTranslator>;
  modals: ReturnType<typeof useAppModals>;
  isEn: boolean;
}

export function useAppShortcuts({ auth, modals, isEn }: UseAppShortcutsProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcuts when typing in inputs, textareas, selects, or editable areas
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable)
      ) {
        return;
      }

      // Ignore shortcuts when any modal is currently open
      if (
        modals.settingsOpen ||
        modals.docsOpen ||
        modals.summaryOpen ||
        modals.profileOpen ||
        modals.historyOpen ||
        modals.stopConfirmOpen ||
        modals.logoutConfirmOpen
      ) {
        return;
      }

      // 1. Ctrl+M: Switch app mode between Translator and Summary Only (strictly Ctrl, not Cmd on Mac)
      if (e.ctrlKey && !e.metaKey && e.key.toLowerCase() === "m") {
        e.preventDefault();
        const nextMode = auth.appMode === "traductor" ? "minuta" : "traductor";
        auth.setAppMode(nextMode);
        toast.success(
          isEn
            ? `Mode switched: ${nextMode === "traductor" ? "Translate + Summary" : "Summary Only"}`
            : `Modo cambiado: ${nextMode === "traductor" ? "Traductor + Minuta" : "Solo Minuta"}`,
          { duration: 2500 }
        );
        return;
      }

      // 2. Space: Start/Stop recording
      if ((e.code === "Space" || e.key === " ") && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        if (e.target instanceof HTMLElement) {
          e.target.blur();
        }
        if (auth.recording) {
          modals.setStopConfirmOpen(true);
        } else {
          auth.toggleRecording();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [auth.appMode, auth.setAppMode, auth.recording, auth.toggleRecording, modals, isEn]);
}
